/**
 * Gebruikersnaamvoorstellen voor kinderen
 * ═══════════════════════════════════════
 *
 * HET PROBLEEM
 *
 * Een gebruikersnaam moet uniek zijn, maar voornamen zijn dat niet. Bij een paar
 * honderd kinderen is elke Pietje, Sanne en Noah al bezet. Het oude gedrag
 * vroeg het kind dan om "je achternaam toe te voegen" — en dat is precies
 * verkeerd:
 *
 *   1. Het is het tegenovergestelde van dataminimalisatie. Een achternaam maakt
 *      een pseudonieme gebruikersnaam direct herleidbaar tot een persoon, en
 *      die naam staat vervolgens op het inlogscherm van een gedeeld apparaat.
 *   2. Het legt het probleem bij een negenjarige. "Verzin iets anders" is voor
 *      een kind geen opdracht die het kan uitvoeren; het gevolg is dat er
 *      willekeurige tekens achter komen die niemand onthoudt.
 *
 * En onthouden is het echte criterium. Een kind dat zijn gebruikersnaam kwijt
 * is, is zijn voortgang kwijt — er is geen e-mailadres om op terug te vallen.
 *
 *
 * DE OPLOSSING: EEN WOORD, GEEN NUMMER
 *
 * `pietje-tijger` in plaats van `pietje7` of `pietje.devries`.
 *
 * Waarom een woord beter werkt dan een cijfer:
 *
 *   - Het is te onthouden. "Pietje tijger" is een beeld; "Pietje zeven" is een
 *     willekeurig getal tussen andere willekeurige getallen. Bij cijfers weet
 *     een kind na de vakantie niet meer of het 7, 17 of 71 was.
 *   - Het is voor te lezen. Een kind noemt zijn naam aan een ouder of
 *     leerkracht. "tijger" komt goed over de telefoon, "7" wordt "zeven" of
 *     "17".
 *   - Het voelt niet als tweede keus. `pietje7` zegt "er waren er al zes voor
 *     jou". `pietje-tijger` is gewoon een naam.
 *   - Het verraadt niets. Een nummer suggereert hoeveel gebruikers er zijn.
 *
 * Het kind hoeft niets te verzinnen: het kiest uit drie voorstellen. Kiezen kan
 * een kind van negen wel, bedenken niet.
 *
 *
 * DE WOORDENLIJST
 *
 * Bewust kort, concreet en positief. Alleen dieren, ruimte en natuur — zaken
 * waar een kind een plaatje bij heeft. Geen woorden die een eigenschap van het
 * kind kunnen suggereren (geen 'snel', 'slim', 'groot'): dat zou een
 * gebruikersnaam maken die iets over de persoon beweert, en op een gedeeld
 * apparaat leest iedereen dat mee.
 *
 * Geen woorden die op elkaar lijken in klank of spelling, want dan wordt
 * voorlezen weer een gokspel.
 */

/** Woorden waar een kind van 9-12 een beeld bij heeft. */
const WORDS = [
  // dieren
  'tijger',
  'panda',
  'dolfijn',
  'vos',
  'uil',
  'egel',
  'zebra',
  'koala',
  'otter',
  'havik',
  'wolf',
  'kikker',
  // ruimte
  'raket',
  'komeet',
  'planeet',
  'maan',
  'ster',
  'saturnus',
  // natuur
  'vulkaan',
  'bliksem',
  'rivier',
  'woestijn',
  'gletsjer',
  'regenboog',
] as const

/**
 * Normaliseer een voornaam naar het toegestane patroon.
 *
 * Hetzelfde patroon als migratie 0002 en de Edge Function: `^[a-z0-9._-]{3,32}$`.
 * Diakrieten worden vervangen in plaats van verwijderd — `José` moet `jose`
 * worden en niet `jos`, want dat is een andere naam.
 */
export function normalizeFirstName(displayName: string): string {
  return displayName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .split(/\s+/)[0] // alleen het eerste woord: dit is een voornaam
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Kies `count` verschillende woorden, willekeurig.
 *
 * `crypto.getRandomValues` in plaats van `Math.random`: niet omdat dit
 * geheim moet zijn, maar omdat een voorspelbare reeks betekent dat twee
 * kinderen die tegelijk registreren dezelfde drie voorstellen krijgen — en dan
 * botsen ze opnieuw, precies wat dit moet voorkomen.
 */
function pickWords(count: number): string[] {
  const pool = [...WORDS]
  const chosen: string[] = []
  const rand = new Uint32Array(count)
  crypto.getRandomValues(rand)

  for (let i = 0; i < count && pool.length > 0; i++) {
    const index = rand[i] % pool.length
    chosen.push(pool[index])
    pool.splice(index, 1)
  }
  return chosen
}

/**
 * Bouw gebruikersnaamvoorstellen voor een voornaam.
 *
 * De eerste keus is altijd de kale voornaam — als `pietje` vrij is, is dat de
 * beste naam die er is en moeten we niets verzinnen. Pas als die bezet is,
 * komen de woordvarianten.
 *
 * Er wordt hier NIET gecontroleerd of een voorstel vrij is. Dat kan de client
 * ook niet betrouwbaar weten (zie de waarschuwing hieronder). De server is de
 * enige die dat kan beslissen, en die weigert met een 409.
 */
export function buildUsernameSuggestions(
  displayName: string,
  options: { includePlain?: boolean; count?: number } = {},
): string[] {
  const base = normalizeFirstName(displayName)
  if (base.length < 2) return []

  const { includePlain = true, count = 3 } = options
  const suggestions = includePlain ? [base] : []

  for (const word of pickWords(count)) {
    const candidate = `${base}-${word}`
    // Het patroon staat maximaal 32 tekens toe. Bij een lange voornaam kappen
    // we de naam níet af — een kind moet zijn eigen naam terugzien — maar slaan
    // we het woord over en vallen terug op minder voorstellen.
    if (candidate.length <= 32) suggestions.push(candidate)
  }

  return suggestions
}

/**
 * ⚠️ WAARSCHUWING BIJ DE BESCHIKBAARHEIDSCONTROLE
 *
 * Het registratieformulier controleert beschikbaarheid nu tegen de LOKALE
 * accountlijst (`isUsernameAvailable`). Dat is sinds de omzetting naar Supabase
 * op twee manieren fout:
 *
 *   - Vals alarm: een naam die alleen in deze browser bestaat wordt geweigerd
 *     terwijl hij op de server vrij is. Dat is wat je ziet als je op één
 *     apparaat meerdere testaccounts hebt gemaakt.
 *   - Gemist alarm: een naam die een ander kind elders al heeft, lijkt vrij. Het
 *     kind vult het hele formulier in en krijgt pas bij verzenden een 409.
 *
 * Een echte controle vraagt een serverside lookup, en die mag niet als open
 * endpoint bestaan: dan is het een manier om te achterhalen welke
 * gebruikersnamen in gebruik zijn (en dus welke kinderen bestaan).
 *
 * De juiste oplossing is niet vooraf controleren maar het weigeren opvangen:
 * bij een 409 laat de server het formulier drie nieuwe voorstellen tonen. Het
 * kind ziet dan één keer "die was net bezet, kies er een" in plaats van een
 * controle die soms liegt.
 */
